// src/api/adminCoupons.ts
import api from './axios';

// ==============================
// 型定義（サーバーの CouponOut/CouponCreate に準拠）
// ==============================

// 日本語: 割引タイプ
export type DiscountType = 'percent' | 'amount' | 'fixed_price' | null;
// 日本語: 返佣タイプ
export type CommissionType = 'percent' | 'amount' | null;
// 日本語: 返佣基準（税/クーポン前後など運用方針に合わせて使用）
export type CommissionBase = 'before' | 'after' | null;

// 日本語: 一覧/詳細のレスポンス（CouponOut 相当）
export interface Coupon {
  id: number;
  name: string;
  description?: string | null;

  // 日本語: 表示用は大文字で返る（バックエンドが upper-case で返却）
  code: string;

  // 日本語: 短縮リンク用のスラッグ（フロントで /c/{link_code} に連結）
  link_code?: string | null;

  discount_type?: DiscountType;
  discount_value?: number | null;

  commission_type?: CommissionType;
  commission_value?: number | null;
  commission_base?: CommissionBase;

  affiliate_id?: number | null;

  // 日本語: サービス/メールドメインの制約（配列）
  allowed_services?: string[] | null;
  allowed_email_domains?: string[] | null;

  visit_type_limit?: string | null;
  company_id?: number | null;

  min_amount?: number | null;
  currency?: string | null;

  starts_at?: string | null; // ISO 文字列
  ends_at?: string | null;   // ISO 文字列

  max_redemptions_total?: number | null;
  max_redemptions_per_user?: number | null;

  note?: string | null;

  is_active: boolean;
  stackable?: boolean;

  created_by?: number | null;
  created_at?: string | null; // ISO
  updated_at?: string | null; // ISO
}

// 日本語: 作成リクエスト（CouponCreate 相当）
export interface CouponCreateInput {
  name: string;
  description?: string | null;

  // 日本語: 入力は任意の大小だが、サーバー側で小文字保存＆大文字返却
  code: string;

  // 日本語: 空ならサーバーが自動生成（小文字保存）
  link_code?: string | null;

  discount_type?: DiscountType;
  discount_value?: number | null;

  commission_type?: CommissionType;
  commission_value?: number | null;
  commission_base?: CommissionBase; // 例: 'after'

  affiliate_id?: number | null;

  allowed_services?: string[] | null;
  visit_type_limit?: string | null;
  company_id?: number | null;
  allowed_email_domains?: string[] | null;

  min_amount?: number | null;
  currency?: string | null;

  starts_at?: string | null; // ISO
  ends_at?: string | null;   // ISO

  max_redemptions_total?: number | null;
  max_redemptions_per_user?: number | null;

  note?: string | null;

  is_active?: boolean; // 既定 true
  stackable?: boolean; // 既定 false
}

// 日本語: 部分更新（PATCH）用
export interface CouponUpdateInput {
  name?: string;
  description?: string | null;

  // 日本語: 更新時も小文字保存される（バックエンド実装）
  code?: string;
  link_code?: string | null;

  discount_type?: DiscountType;
  discount_value?: number | null;

  commission_type?: CommissionType;
  commission_value?: number | null;
  commission_base?: CommissionBase;

  allowed_services?: string[] | null;
  visit_type_limit?: string | null;
  company_id?: number | null;
  allowed_email_domains?: string[] | null;

  min_amount?: number | null;
  currency?: string | null;

  starts_at?: string | null;
  ends_at?: string | null;

  max_redemptions_total?: number | null;
  max_redemptions_per_user?: number | null;

  note?: string | null;

  stackable?: boolean;
  is_active?: boolean;
}

// ==============================
// API 関数
// ==============================

// 日本語: クーポン一覧（管理者用）
export const listCoupons = async (params: {
  q?: string;
  active?: boolean;         // 日本語: true/false をそのままクエリへ
  service?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<Coupon[]> => {
  const res = await api.get('/admin/coupons', { params });
  return res.data;
};

// 日本語: クーポン詳細取得
export const getCoupon = async (couponId: number): Promise<Coupon> => {
  const res = await api.get(`/admin/coupons/${couponId}`);
  return res.data;
};

// 日本語: クーポン作成
export const createCoupon = async (payload: CouponCreateInput): Promise<Coupon> => {
  const res = await api.post('/admin/coupons', payload);
  return res.data;
};

// 日本語: クーポン部分更新（PATCH）
export const updateCoupon = async (couponId: number, payload: CouponUpdateInput): Promise<Coupon> => {
  const res = await api.patch(`/admin/coupons/${couponId}`, payload);
  return res.data;
};

// 日本語: 有効/無効の切替（軽量トグル）
export const toggleCouponStatus = async (couponId: number, isActive: boolean): Promise<Coupon> => {
  const res = await api.patch(`/admin/coupons/${couponId}/status`, { is_active: isActive });
  return res.data;
};

// 日本語: クーポン削除
export const deleteCoupon = async (couponId: number): Promise<{ deleted: boolean; id: number }> => {
  const res = await api.delete(`/admin/coupons/${couponId}`);
  return res.data;
};

// 日本語: サーバー側コード自動生成（/api/admin/coupons/generate-code）
export const generateCode = async (): Promise<{ code: string }> => {
  const res = await api.get('/admin/coupons/generate-code');
  return res.data;
};

// ==============================
// ユーティリティ
// ==============================

// 日本語: 短縮リンクURLを生成（フロントの現在オリジンを使用）
// 例: https://localhost:3000/c/abc123
export const buildShortUrl = (linkCode?: string | null): string | null => {
  if (!linkCode) return null;
  try {
    const origin =
      typeof window !== 'undefined' && window.location && window.location.origin
        ? window.location.origin
        : '';
    if (!origin) return `./c/${linkCode}`;
    return `${origin}/c/${linkCode}`;
  } catch {
    return `./c/${linkCode}`;
  }
};
